import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Fab,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import config from "../config/config";

interface Message {
  role: "bot" | "user";
  text: string;
  quickReplies?: string[];
}

export interface ChatWidgetProps {
  isOpen?: boolean;
  onToggle?: () => void;
  initialMessage?: string;
  onMessageSent?: () => void;
}

const WELCOME_MESSAGE: Message = {
  role: "bot",
  text: "Hola. Soy el asistente técnico de Coati para integraciones de operadores logísticos. "
    + "Puedo ayudarte con endpoints, autenticación, errores y el flujo completo de integración.",
  quickReplies: [
    "Cómo me registro como OLS",
    "Qué endpoints tengo que implementar",
    "Cómo funciona la autenticación",
    "Ver guía de integración en 5 pasos",
    "Cómo envío actualizaciones de estado",
    "Tengo un error, necesito ayuda",
  ],
};

// eslint-disable-next-line max-lines-per-function
const ChatWidget = ({ isOpen: externalIsOpen, onToggle, initialMessage, onMessageSent }: ChatWidgetProps) => {
  const [messages, setMessages] = useState<Message[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [internalOpen, setInternalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const pendingMessage = useRef<string | undefined>();

  const isOpen = externalIsOpen ?? internalOpen;
  const handleToggle = onToggle ?? (() => setInternalOpen((v) => !v));

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    pendingMessage.current = initialMessage;
  }, [initialMessage]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;

    const userMessage: Message = { role: "user", text };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(`${config.AUTH_API_BASE_URL}/partnerbot/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      const data = await res.json();
      const botMessage: Message = {
        role: "bot",
        text: data.response,
        quickReplies: data.quickReplies,
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      setLoading(false);
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: "Error al conectar con el servidor. Intentá de nuevo." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Send queued message when chat opens (e.g. from a landing page quick-reply chip)
  useEffect(() => {
    if (isOpen && pendingMessage.current) {
      const msg = pendingMessage.current;
      pendingMessage.current = undefined;
      sendMessage(msg);
      onMessageSent?.();
    }
  }, [isOpen]);

  return (
    <>
      <Fab
        color="primary"
        aria-label="Abrir asistente de integración"
        onClick={handleToggle}
        sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1000 }}
      >
        <span className="material-symbols-rounded">forum</span>
      </Fab>

      {isOpen && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 92,
            right: 24,
            width: 380,
            height: 560,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            zIndex: 1000,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              CoatiBot — Asistente de integración
            </Typography>
            <Box
              component="button"
              onClick={handleToggle}
              aria-label="Cerrar asistente"
              sx={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "primary.contrastText",
                display: "flex",
                alignItems: "center",
                p: 0,
              }}
            >
              <span className="material-symbols-rounded" style={{ fontSize: 20 }}>close</span>
            </Box>
          </Box>

          <Box
            role="log"
            aria-live="polite"
            sx={{ flex: 1, overflowY: "auto", p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}
          >
            {messages.map((msg, i) => (
              <Box key={i}>
                <Box
                  sx={{
                    alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                    bgcolor: msg.role === "user" ? "primary.main" : "grey.100",
                    color: msg.role === "user" ? "primary.contrastText" : "text.primary",
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    maxWidth: "85%",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    ml: msg.role === "user" ? "auto" : 0,
                    fontSize: 13,
                  }}
                >
                  {msg.text}
                </Box>

                {msg.quickReplies && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                    {msg.quickReplies.map((qr, j) => (
                      <Chip
                        key={j}
                        label={qr}
                        variant="outlined"
                        color="primary"
                        size="small"
                        clickable
                        onClick={() => sendMessage(qr)}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            ))}
            <div ref={bottomRef} />
          </Box>

          <Box sx={{ p: 1, borderTop: 1, borderColor: "grey.200", display: "flex", gap: 0.75 }}>
            <TextField
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage(input)}
              placeholder="Escribí tu pregunta..."
              disabled={loading}
              size="small"
              fullWidth
              variant="outlined"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendMessage(input)}
              disabled={loading}
              sx={{ minWidth: 48, px: 1.5 }}
            >
              {loading ? "..." : "→"}
            </Button>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default ChatWidget;
